import { makeObservable, observable, action, computed } from 'mobx';
import { dataAccess } from 'ts/core/dataAccess/dataAccess';
import { IProject, IRepository, Repositories } from 'ts/types/repositories';

const LOCAL_STORAGE_KEY = 'repoProjectStore';

export type RepoProjectStoreFilters = Record<string, boolean>;

export interface IRepoProjectStore {
  repoProjectList: Repositories;
  filteredRepoProjectList: Repositories;
  searchQuery: string;
  filters: RepoProjectStoreFilters;
  currentPage: Record<string, number>;
  accordionState: Record<string, boolean>;
  isAccordionOpen: (repoId: string) => boolean;
  toggleAccordion: (repoId: string) => void;
  setAccordionState: (repoId: string, isOpen: boolean) => void;
  setSearchQuery: (query: string) => void;
  getCurrentPage: (repoId: string) => number;
  setCurrentPage: (repoId: string, page: number) => void;
  setRepoProjectList: () => void;
  getLogById: (projectId: number) => Promise<any>;
}

const defaultRepoProjectStoreFilters: RepoProjectStoreFilters = {
  activeOnly: true,
  hideDisabled: true,
  errorsOnly: false,
};

class RepoProjectStore implements IRepoProjectStore {
  repoProjectList: Repositories = [];
  searchQuery: string = '';
  filters: RepoProjectStoreFilters = { ...defaultRepoProjectStoreFilters };
  currentPage: Record<string, number> = {};
  accordionState: Record<string, boolean> = {};

  constructor() {
    makeObservable(this, {
      repoProjectList: observable,
      searchQuery: observable,
      filters: observable,
      currentPage: observable,
      accordionState: observable,
      setRepoProjectList: action,
      setSearchQuery: action,
      setAccordionState: action,
      setCurrentPage: action,
      getCurrentPage: action,
      getLogById: action,
      setActiveOnly: action,
      setHideDisabled: action,
      filteredRepoProjectList: computed,
    });

    this.loadFromLocalStorage();
  }

  get filteredRepoProjectList() {
    if (!this.repoProjectList) return [];

    const query = this.searchQuery.toLowerCase();
    return this.repoProjectList.map((repo: IRepository) => ({
      ...repo,
      projects: (repo.projects || []).filter((project: IProject) =>
        (!query || project.name.toLowerCase().includes(query))
        && (!this.filters.activeOnly || project.active)
        && (!this.filters.hideDisabled || !project.disabled)
        && (!this.filters.errorsOnly || project.last_error)
      ),
    }));
  }

  async setRepoProjectList() {
    const [repoProjectList] = await dataAccess.getRepositoryProjectList();
    this.repoProjectList = repoProjectList;
  }

  async getLogById(projectId: number) {
    return dataAccess.getLogByProjectId(projectId);
  }

  private saveToLocalStorage() {
    const state = {
      searchQuery: this.searchQuery,
      currentPage: this.currentPage,
      accordionState: this.accordionState,
      filters: this.filters,
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  }

  private loadFilters(filters: any) {
    filters = filters ?? {};
    const value = { ...defaultRepoProjectStoreFilters };
    for (const key of Object.keys(value)) {
      value[key] = filters[key] ?? value[key];
    }
    this.filters = value;
  }

  private loadFromLocalStorage() {
    if (typeof window === 'undefined') return;
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return;

    try {
      const state = JSON.parse(raw);
      this.searchQuery = state.searchQuery || '';
      this.currentPage = state.currentPage || {};
      this.accordionState = state.accordionState || {};
      // this.activeOnly = state.filters?.activeOnly || true;
      this.loadFilters(state.filters);
    } catch (err) {
      console.warn('Failed to load repoProjectStore from localStorage', err);
    }
  }

  setSearchQuery(query: string) {
    this.searchQuery = query;
    this.saveToLocalStorage();
  }

  setCurrentPage(repoId: string, page: number) {
    this.currentPage = { ...this.currentPage, [repoId]: page };
    this.saveToLocalStorage();
  }

  getCurrentPage(repoId: string): number {
    return this.currentPage[repoId] ?? 1;
  }

  toggleAccordion(repoId: string) {
    const current = this.isAccordionOpen(repoId);
    this.setAccordionState(repoId, !current);
  }

  setAccordionState(repoId: string, isOpen: boolean) {
    this.accordionState[repoId] = isOpen;
    this.saveToLocalStorage();
  }

  isAccordionOpen(repoId: string) {
    return !!this.accordionState[repoId];
  }

  setActiveOnly(value: boolean) {
    this.filters.activeOnly = value;
    this.saveToLocalStorage();
  }

  setHideDisabled(value: boolean) {
    this.filters.hideDisabled = value;
    this.saveToLocalStorage();
  }

  setErrorsOnly(value: boolean) {
    this.filters.errorsOnly = value;
    this.saveToLocalStorage();
  }
}

const repoProjectStore = new RepoProjectStore();

export default repoProjectStore;